import { Stack } from '@mui/material';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

/**
 * Which background the group is sitting on. Drives the unselected button's text
 * colour, since that button is transparent and cannot supply its own contrast.
 */
export type ToggleButtonSurface = 'dark' | 'light';

interface IToggleButtonContextProps {
  currentValue: string;
  setValue(value: string): void;
  surface: ToggleButtonSurface;
}

const ToggleButtonContext = createContext<IToggleButtonContextProps>({
  currentValue: '',
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setValue: (value: string) => {},
  surface: 'dark',
});

export const useToggleButton = () => {
  return useContext(ToggleButtonContext);
};

export interface ToggleButtonGroupProps {
  /**
   * Uncontrolled seed. Read once at mount, so only use this when the value is
   * already known by then -- a value that arrives later (a form reset, an async
   * fetch) will not be picked up. Pass `value` instead in that case.
   */
  defaultValue?: string;
  /**
   * Controlled value. When supplied the group renders this instead of its own
   * internal state, which makes it safe for values seeded after mount and means
   * re-selecting the already-highlighted button still reports through `onClick`
   * (the uncontrolled path swallows that click via React's state bailout).
   */
  value?: string;
  children: ReactNode;
  onClick?: (value: string) => void;
  /**
   * Background the group renders against. Defaults to 'dark' to match the
   * quick-filter strip this component was originally built for; pass 'light'
   * when dropping it onto a white form, or the unselected button's white label
   * disappears into the page.
   */
  surface?: ToggleButtonSurface;
}

export function ToggleButtonGroup({
  defaultValue = '',
  value,
  children,
  onClick,
  surface = 'dark',
}: ToggleButtonGroupProps) {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue);
  const currentValue = isControlled ? value : internalValue;

  // Uncontrolled only: report the internal value as it changes (including the
  // mount-time seed). Controlled callers own their value, so reporting it back
  // here would fight the parent.
  useMemo(() => {
    if (!isControlled && internalValue) {
      onClick?.(internalValue);
    }
  }, [internalValue, isControlled]);

  const setValue = useCallback(
    (next: string) => {
      if (isControlled) {
        onClick?.(next);
      } else {
        setInternalValue(next);
      }
    },
    [isControlled, onClick]
  );

  return (
    <ToggleButtonContext.Provider value={{ currentValue, setValue, surface }}>
      <Stack direction="row" spacing={2}>
        {children}
      </Stack>
    </ToggleButtonContext.Provider>
  );
}
